import type {
  CallAudit, CallAuditExchange, CallAuditFailure, CallAuditResponse, SessionReport,
} from './tracking-demo.models';

/**
 * transcript-proof.util — maps each transcript turn to the audit proof behind it,
 * so the Transcript tab can show WHY a response was generated and from WHICH source
 * (knowledge lookups vs the system prompt) inline, per response.
 *
 * Pure and unit-tested like rag-log.util. Reads `callAudit.exchanges` — the compact
 * turn↔lookup map the backend writes regardless of judge mode — and joins it with
 * `responses` (judge verdicts, when the judge ran) and `failures`.
 *
 * INDEX ALIGNMENT: backend exchange indices refer to the transcript with empty-text
 * turns dropped (`build_exchanges` filters them). The map returned here is keyed by
 * the RAW transcript array index the template iterates, so the join replays that
 * same filter rather than assuming the two match.
 */

/** Everything the inline proof panel needs for one turn. */
export interface TurnProof {
  exchangeIndex: number;
  /** Raw-transcript indices of the query turns this response answers. */
  queryTurns: number[];
  lookups: Array<{ index: number; exact: boolean }>;
  detectorFlags: string[];
  graded: boolean;
  /** The judge's verdict for this exchange — absent in detectors-only mode. */
  response?: CallAuditResponse;
  failures: CallAuditFailure[];
  /** red = a failure names this exchange; green = knowledge-grounded, clean;
   *  gray = answered with no knowledge lookup (prompt / model knowledge). */
  state: 'failure' | 'grounded' | 'no-lookup';
}

/** One human-readable line naming the data source of the response. */
export function proofSourceLabel(p: TurnProof): string {
  if (!p.lookups.length) {
    return 'No knowledge lookup — answered from the system prompt / model knowledge';
  }
  const inferred = p.lookups.filter((l) => !l.exact).length;
  const n = p.lookups.length;
  return `Knowledge base — ${n} lookup${n === 1 ? '' : 's'}`
    + (inferred ? ` (${inferred} inferred pairing${inferred === 1 ? '' : 's'})` : '');
}

/**
 * Raw-transcript-index → TurnProof. The proof anchors on the FIRST agent turn of the
 * exchange's answer; an UNANSWERED exchange (agent_silent) anchors on its LAST query
 * turn, so the failure is visible in the transcript exactly where the silence was.
 * Empty map when the stored audit predates the exchanges key (re-run the audit).
 */
export function buildTurnProofMap(
  audit: CallAudit | null | undefined,
  transcript: SessionReport['transcript'] | null | undefined,
): Map<number, TurnProof> {
  const map = new Map<number, TurnProof>();
  const exchanges = audit?.exchanges;
  if (!Array.isArray(exchanges) || !exchanges.length) return map;

  // Replay the backend's normalization: filtered index -> raw index.
  const rawIndex: number[] = [];
  (transcript ?? []).forEach((t, i) => {
    if (String(t?.text ?? '').trim()) rawIndex.push(i);
  });

  const responseByExchange = new Map<number, CallAuditResponse>();
  for (const r of audit?.responses ?? []) responseByExchange.set(r.exchangeIndex, r);
  const failuresByExchange = new Map<number, CallAuditFailure[]>();
  for (const f of audit?.failures ?? []) {
    if (f.exchangeIndex == null) continue;
    const list = failuresByExchange.get(f.exchangeIndex) ?? [];
    list.push(f);
    failuresByExchange.set(f.exchangeIndex, list);
  }

  for (const ex of exchanges) {
    const anchorFiltered = ex.answerTurns.length
      ? ex.answerTurns[0]
      : ex.queryTurns[ex.queryTurns.length - 1];
    const anchor = rawIndex[anchorFiltered];
    if (anchor === undefined) continue; // audit/transcript drifted — no proof beats a wrong one
    const failures = failuresByExchange.get(ex.index) ?? [];
    map.set(anchor, {
      exchangeIndex: ex.index,
      queryTurns: ex.queryTurns.map((i) => rawIndex[i]).filter((i) => i !== undefined),
      lookups: ex.lookups ?? [],
      detectorFlags: ex.detectorFlags ?? [],
      graded: !!ex.graded,
      response: responseByExchange.get(ex.index),
      failures,
      state: failures.length ? 'failure' : (ex.lookups?.length ? 'grounded' : 'no-lookup'),
    });
  }
  return map;
}

/** Type re-export so the component's template helpers stay terse. */
export type { CallAuditExchange };
